import { ChargedAgainstOptionTypeEnum } from 'features/transactions/types';
import { IAuthorizationWithCrosses } from '../types';

export interface AuthorizationBalances {
	pendingToCross: number;
	activeCrossed: number;
	totalRefunded: number;
	/** Monto con el que se creó la autorización (histórico). */
	originalAuthorized: number;
	/** Saldo vigente según API (`amountAuthorized`, ya neteado por devoluciones). */
	currentBalance: number;
	/** Igual a `currentBalance`; no restar `totalRefunded` otra vez. */
	netAuthorized: number;
	maxRefundable: number;
	/** Cobro por pasarela (dLocal): no cruza e-tickets. */
	isPaymentGateway: boolean;
	canRefund: boolean;
	canCross: boolean;
	hasActiveCrosses: boolean;
	isFullyRefunded: boolean;
	isPartiallyRefunded: boolean;
}

export const computeAuthorizationBalances = (authorization: IAuthorizationWithCrosses): AuthorizationBalances => {
	const pendingToCross = parseFloat(String(authorization.pendingToCross)) || 0;
	const currentBalance = Math.max(0, authorization.amountAuthorized);
	/**
	 * Las autorizaciones por pasarela (dLocal) no cruzan boletos: el back las crea con pendingToCross = 0
	 * y permite devolver hasta amountAuthorized. No hay que leer ese 0 como "todo cruzado".
	 */
	const isPaymentGateway = authorization.chargedAgainstOptionType === ChargedAgainstOptionTypeEnum.PaymentGateway;
	/** Saldo actualmente cruzado en e-tickets (fuente de verdad: API, no el historial de filas). */
	const activeCrossed = isPaymentGateway ? 0 : Math.max(0, currentBalance - pendingToCross);
	const totalRefunded = authorization.refunded?.reduce((sum, refund) => sum + refund.amountRefunded, 0) ?? 0;

	const originalAuthorized =
		authorization.initialAmountAuthorized != null && authorization.initialAmountAuthorized > 0
			? authorization.initialAmountAuthorized
			: authorization.amountAuthorized + totalRefunded;

	const netAuthorized = currentBalance;
	const maxRefundable = isPaymentGateway ? currentBalance : Math.max(0, Math.min(pendingToCross, currentBalance));

	const isFullyRefunded = totalRefunded > 0 && currentBalance === 0 && pendingToCross === 0 && activeCrossed === 0;
	const isPartiallyRefunded = totalRefunded > 0 && currentBalance > 0;

	return {
		pendingToCross,
		activeCrossed,
		totalRefunded,
		originalAuthorized,
		currentBalance,
		netAuthorized,
		maxRefundable,
		isPaymentGateway,
		canRefund: maxRefundable > 0,
		canCross: currentBalance > 0 && pendingToCross > 0,
		hasActiveCrosses: activeCrossed > 0,
		isFullyRefunded,
		isPartiallyRefunded,
	};
};
